// The twelve career jobs' words and numbers, WITHOUT their yards.
//
// Split out of levels.ts so a screen that only needs a job's number and title
// (HQ's "Next up: job 3, Back yard with beds", its "of 36 stars") does not pull
// every yard's layout JSON — about 240 KB — into the app's first load.
// levels.ts composes these with the yards into LEVELS; edit the text here.
import type { Level } from '../types';

// Milestone E: `headLines` is the Heads tray (and the Designer's) nozzle lines for a lesson, the
// lesson's line first; `drip` says whether the job has a Drip station (the Designer and the strip
// read it).

export type LevelText = Omit<Level, 'omissions' | 'existing'>;

export const LEVEL_TEXT: readonly LevelText[] = [
  {
    n: 1, key: 'nature-strip', title: 'Nature strip only', layoutKey: 'kerbside-nature-strip', difficulty: 'apprentice',
    brief: 'First home, one tap, a council strip that\'s mostly dust. Keep the footpath dry.',
    teaches: 'Head-to-head coverage and keeping water off hardscape: strip nozzles, one zone, a tap timer.',
    // Strips throw even rectangles, but where two meet at a strip's corner the ends double up: a
    // strips-only nature strip reads DU_lq in the high 60s (the Designer's eight strips: 67).
    duTarget: 65, stationsMax: 1,
    headLines: ['Sweeper Strips'], drip: false,
  },
  {
    n: 2, key: 'front-lawn', title: 'Front lawn', layoutKey: 'kerbside-front-lawn', difficulty: 'apprentice',
    brief: 'Retired teacher, buffalo front lawn, grandkids visiting at Christmas. The car stays dry.',
    teaches: 'Adding up head flows against the tap: two zones of Sweeper 20s, one box, a 6-station controller.',
    duTarget: 70, stationsMax: 6,
    headLines: ['Sweeper 20', 'Sweeper 10', 'Sweeper 30'], drip: false,
  },
  {
    n: 3, key: 'back-yard-beds', title: 'Back yard with beds', layoutKey: 'gumtree-heights-backyard', difficulty: 'tradie',
    brief: 'Family of five, kikuyu under a gum, beds on three sides, pots on the patio.',
    teaches: 'Sprays or rotary nozzles, your first drip zone, and the Drip Control Kit: filter before regulator.',
    duTarget: 70, stationsMax: 6,
    drip: true,
  },
  {
    n: 4, key: 'veggie-garden', title: 'Veggie garden', layoutKey: 'kerbside-veggie-garden', difficulty: 'tradie',
    brief: 'Retiree couple, buffalo lawn, big veggie patch, low pressure street, clay you could throw pots with.',
    teaches: 'Drip tape rows, button drippers for pots, and cycle & soak on clay.',
    duTarget: 70, stationsMax: 6,
    drip: true,
  },
  {
    n: 5, key: 'sloped-block', title: 'Sloped block', layoutKey: 'ridge-rd-slope', difficulty: 'tradie',
    brief: 'Twelve per cent fall to the street. The old system drained itself down the driveway every morning.',
    teaches: 'Rise costs about 10 kPa a metre: size for the top head, drain tap low, air relief high.',
    duTarget: 70, stationsMax: 6,
    drip: true,
  },
  {
    n: 6, key: 'corner-block', title: 'Corner block', layoutKey: 'federation-drive-corner', difficulty: 'tradie',
    brief: 'Two street frontages, a side strip nobody can mow straight, and a path the owner won\'t let you dig.',
    teaches: 'Strip and corner nozzles, looped laterals, and using the sleeve instead of boring under the path.',
    // Two lawns squeezed between side strips: the strips' fringe lands on the lawn edges and the
    // rotary heads can't square off an L-shaped patch (the Designer's best: 64).
    duTarget: 62, stationsMax: 6,
    drip: true,
  },
  {
    n: 7, key: 'big-block', title: 'Big block, low flow', layoutKey: 'federation-drive-big-block', difficulty: 'designer',
    brief: 'Fourteen hundred square metres of kikuyu on a fifteen-litre tap. The meter has a spare branch.',
    teaches: 'When one tap can\'t do it in a night: a second point of connection, with its own backflow.',
    duTarget: 70, stationsMax: 16,
    drip: true,
  },
  {
    n: 8, key: 'retrofit', title: 'Retrofit', layoutKey: 'grevillea-ave-retrofit', difficulty: 'designer',
    brief: 'Weatherboard house, 600 kPa street, a system that fogs the neighbours at noon. Fix it under $900.',
    teaches: 'Diagnose misting sprays, mixed families on one zone, a dead head and no backflow, on a budget.',
    duTarget: 70, stationsMax: 4, budgetAud: 900,
    drip: true,
  },
  {
    n: 9, key: 'bowls-green', title: 'Bowls green', layoutKey: 'bay-bowls-green', difficulty: 'designer',
    brief: 'Bay Bowls Club. A sand green that must roll true, and members who check with a spirit level.',
    teaches: 'Gear-drive rotors from the bank, matched nozzles, pressure regulation, and DU 80 or bust.',
    duTarget: 80, stationsMax: 8,
    drip: true,
  },
  {
    n: 10, key: 'school-oval', title: 'School oval', layoutKey: 'kerbside-primary-oval', difficulty: 'designer',
    brief: 'Seven thousand square metres of kikuyu, a synthetic pitch, and a principal who wants it green by footy.',
    teaches: 'Big rotors, a 32 mm mainline, sub-boxes, 12 stations, and a night that isn\'t quite long enough.',
    // A rotor lattice can't follow the oval's curved edge, and twelve stations on one meter mean
    // low-flow rotors: 146 of them reach DU 73 at best (every size and spacing tried).
    duTarget: 72, stationsMax: 12,
    drip: false,
  },
  {
    n: 11, key: 'caravan-park', title: 'Caravan park', layoutKey: 'foreshore-caravan-park', difficulty: 'master',
    brief: 'A lawn on every site, a power bollard on every lawn, and guests who move the hose.',
    teaches: 'Many small zones, a master valve and a flow sensor, and more zones than one meter gets through in a night.',
    duTarget: 70, stationsMax: 16,
    drip: true,
  },
  {
    n: 12, key: 'community-garden', title: 'Community garden', layoutKey: 'memorial-park-community-garden', difficulty: 'master',
    brief: 'Eighteen plots, eighteen opinions, one committee, and a meter branch that needs a plumber.',
    teaches: 'Sixteen stations of mixed drip, pots and lawn, with a plumber-installed RPZ at the meter.',
    duTarget: 70, stationsMax: 16,
    drip: true,
  },
];
